import {IModalService, IModalSettings} from "angular-ui-bootstrap";
import {TradingSystemsDialogController} from "../dialog/TradingSystemsDialogController";
import {CookbookService} from "../../services/cookbook/CookbookService";
import {SelectedTradingSystem} from "../models/SelectedTradingSystem";
import {StrategiesService} from "../../services/cookbook/StrategiesService";
import {SelectedSystemType} from "../models/SelectedSystemType";
import {StrategyLegTemplate, StrategyTemplate} from "../../services/cookbook/models/StrategyTemplate";
import {ExpiryService} from "../../services/expiry/ExpiryService";
import {Expiries} from "../../services/expiry/Expiries";
import {Recipe, RecipeTrade} from "../../services/cookbook/models/Recipe";
import moment = require("moment");

const IRON_CONDOR_NAME = "iron_condor";
const LITT_SHORT_NAME = "litt_short";
const LITT_LONG_NAME = "litt_long";

export class TradingSystemsDialogService {

    public static id = "tradingSystemsDialogService";

    /** @ngInject */
    public constructor(private $uibModal: IModalService,
                       private cookbookService: CookbookService,
                       private expiryService: ExpiryService,
                       private strategiesService: StrategiesService) {
    }

    public async open(): Promise<Recipe> {
        const options: IModalSettings = {
            templateUrl: "app/trading-systems/dialog/TradingSystemsDialogTemplate.html",
            backdrop: "static",
            size: "fs",
            windowClass: "trading-systems-dialog",
            controllerAs: "ctrl",
            controller: TradingSystemsDialogController
        };
        const instance = this.$uibModal.open(options);
        const strategiesRequest = this.strategiesService.strategies();
        const expiriesRequest = this.expiryService.get("ASX");
        const result: SelectedTradingSystem = await instance.result;
        const strategies = await strategiesRequest;
        const template = await this.retrieveStrategyTemplate(result, strategies);
        const code = result.system.code;
        const expiries = await expiriesRequest;
        const daysOut = this.calculateDaysOut(result);
        const strategyExpiry = this.findExpiry(code, expiries, daysOut);
        return this.createRecipe(code, template, strategyExpiry);
    }

    private calculateDaysOut(tradingSystem: SelectedTradingSystem) {
        switch (tradingSystem.type) {
            case SelectedSystemType.IRON_CONDOR:
                return 20;
            case SelectedSystemType.LITT_LONG:
            case SelectedSystemType.LITT_SHORT:
                return 30;
            default:
                return 7;
        }
    }

    private async retrieveStrategyTemplate(result: SelectedTradingSystem,
                                           strategies: Array<StrategyTemplate>): Promise<StrategyTemplate> {
        switch (result.type) {
            case SelectedSystemType.IRON_CONDOR:
                return strategies.find((str) => str.shortName === IRON_CONDOR_NAME)!;
            case SelectedSystemType.LITT_LONG:
                return strategies.find((str) => str.shortName === LITT_LONG_NAME)!;
            case SelectedSystemType.LITT_SHORT:
                return strategies.find((str) => str.shortName === LITT_SHORT_NAME)!;
        }
    }

    private findExpiry(code: string, expiries: Expiries, daysOut: number): string {
        const underlyingExpiries = expiries.forUnderlying(code);
        const expiriesWithoutWeeklies = underlyingExpiries.ofType("MONTHLY", "QUARTERLY");
        const today = moment();
        const afterDate = today.add(daysOut, "days");
        const afterDateString = afterDate.format("YYYY-MM-DD");
        const expiresAfterDaysOut = expiriesWithoutWeeklies.after(afterDateString);
        return expiresAfterDaysOut.first;
    }

    private createRecipe(code: string, template: StrategyTemplate, expiry: string): Recipe {
        const legs: Array<RecipeTrade> = template.legs.map(this.buildLeg.bind(this));
        return {
            name: template.shortName,
            longName: template.fullName,
            expiryDate: expiry,
            code,
            style: null,
            legs
        };
    }

    private buildLeg(leg: StrategyLegTemplate): RecipeTrade {
        return {
            priceWeight: leg.priceWeight,
            size: leg.size,
            optionType: leg.optionType,
            timeOffset: leg.timeOffset || 0
        };
    }
}
